// @noflow
import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'debounce';
import {
    SearchFilter,
    RefinementBarProvider,
    RefinementBarConsumer,
    RefinementBarUI,
    AvatarAsyncSelectFilter,
} from '@atlaskit/refinement-bar';
import { type OptionsType } from '@atlaskit/select';
import { withApollo } from 'react-apollo';
import styled from 'styled-components';
import { token } from '@atlaskit/tokens';
import { orderBy, uniqBy } from 'lodash';
import LabelsAsyncSelect from '../refinement-bar/fields/LabelsAsyncSelect';
import { loadLabelsOptionsWithIdAsValue } from '../select/LabelSelect';
import { loadProjectsOptions } from '../select/ProjectSelect';

const RefinementBarWrapper = styled.div`
    button {
        background-color: ${token('color.background.neutral')};
        color: ${token('color.text')};
    }
    button:hover {
        background-color: ${token('color.background.neutral.hovered')};
        color: ${token('color.text')};
    }
    button:active {
        background-color: ${token('color.background.neutral.hovered')};
        color: ${token('color.text')};
    }
    input {
        background-color: ${token('color.background.neutral')};
        color: ${token('color.text')};
    }
    input:hover {
        background-color: ${token('color.background.neutral.hovered')};
    }
    input:active {
        background-color: ${token('color.background.neutral.hovered')};
    }
    input:focus {
        background-color: ${token('color.background.neutral.hovered')};
    }
`;

type TableFiltersProps = {
    value: any,
    shouldGroupByLabels: boolean,
    showGroupByLabelsToogle: boolean,
    renderingWithinProjectContext: boolean,
    handleChange: any,
    client: any,
};

const TableFilters = ({
    value,
    shouldGroupByLabels,
    showGroupByLabelsToogle,
    renderingWithinProjectContext,
    handleChange,
    client,
}: TableFiltersProps) => {
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [filterKeys, setFilterKeys] = useState(['search']);

    const loadProjectOptions = (searchText: string, callback: (options: OptionsType[]) => void) => {
        loadProjectsOptions(searchText).then((options) => {
            const filteredSelectedProjects = searchText?.length
                ? (selectedProjects ?? []).filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase()))
                : selectedProjects ?? [];
            return callback(uniqBy([...filteredSelectedProjects, ...(options ?? [])], 'value'));
        });
    };

    const loadLabelsOptions = (searchText: string, callback: (options: OptionsType[]) => void) => {
        loadLabelsOptionsWithIdAsValue(client, searchText).then((options) => {
            const filteredSelectedLabels = searchText?.length
                ? (selectedLabels ?? []).filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase()))
                : selectedLabels ?? [];
            const opts = uniqBy([...filteredSelectedLabels, ...(options ?? [])], 'value');
            return callback(opts);
        });
    };

    const debouncedLoadProjectsOptions = debounce(loadProjectOptions, 300);
    const debouncedLoadLabelsOptions = debounce(loadLabelsOptions, 300);

    const fieldConfig = useMemo(() => {
        return {
            search: {
                label: 'Search',
                type: SearchFilter,
            },
            ...(!renderingWithinProjectContext
                ? {
                      projects: {
                          type: AvatarAsyncSelectFilter,
                          label: 'Projects',
                          loadOptions: debouncedLoadProjectsOptions,
                          cacheOptions: true,
                          defaultOptions: true,
                          placeholder: 'Start typing to find more',
                      },
                  }
                : {}),
            ...(!renderingWithinProjectContext
                ? {
                      labels: {
                          type: LabelsAsyncSelect,
                          label: 'Labels',
                          loadOptions: debouncedLoadLabelsOptions,
                          cacheOptions: true,
                          defaultOptions: true,
                          placeholder: 'Start typing to find more',
                          showToogle: !!showGroupByLabelsToogle,
                          shouldGroupByLabels,
                          onToggle: (toogleValue) => {
                              handleChange(toogleValue, { action: 'toogle-group-labels' });
                          },
                      },
                  }
                : {}),
        };
    }, [
        debouncedLoadLabelsOptions,
        debouncedLoadProjectsOptions,
        handleChange,
        shouldGroupByLabels,
        showGroupByLabelsToogle,
        renderingWithinProjectContext,
    ]);

    const debounceSearchTerm = debounce((searchTerm: Object, meta: Object) => {
        handleChange(searchTerm, meta);
    }, 600);

    useEffect(() => {
        if (!renderingWithinProjectContext) {
            setFilterKeys((keys) => [...keys, 'projects', 'labels']);
        }
    }, [renderingWithinProjectContext]);

    useEffect(() => {
        return () => {
            if (typeof debounceSearchTerm.clear === 'function') {
                debounceSearchTerm.clear();
            }
        };
    }, [debounceSearchTerm]);

    const onFilterChange = (filterValue: Object, meta: Object) => {
        if (meta?.key === 'search') {
            debounceSearchTerm(filterValue, meta);
        } else {
            setSelectedProjects(orderBy(filterValue?.projects ?? [], [({ label }) => label.toLowerCase()], ['asc']));
            setSelectedLabels(orderBy(filterValue?.labels ?? [], [({ label }) => label.toLowerCase()], ['asc']));
            handleChange(filterValue, meta);
        }
    };

    return (
        <RefinementBarWrapper>
            <RefinementBarProvider
                fieldConfig={fieldConfig}
                irremovableKeys={filterKeys}
                onChange={onFilterChange}
                value={value}
            >
                <RefinementBarUI />
                <RefinementBarConsumer>{({ barValue }) => <div value={barValue} />}</RefinementBarConsumer>
            </RefinementBarProvider>
        </RefinementBarWrapper>
    );
};

export default withApollo(TableFilters);
